import { useEffect, useState } from 'react';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';

import FontIcon from 'components/icons';
import Link from 'components/link/link';

type ItemsProps = {
  classes: string[],
  description: string,
  title: string,
  url: string,
};

type MenuDropDownProps = {
  bottomItem?: ItemsProps,
  items: ItemsProps[],
  title: string,
  openMenu: string,
  setOpenMenu: (value: string) => void,
};

export const MenuDropDown = ({ title, items, bottomItem, openMenu, setOpenMenu }: MenuDropDownProps) => {
  const [activateMenu, setActivateMenu] = useState(false);
  const [clickMenu, setClickMenu] = useState(false);
  const breakpoints = useBreakpoint();

  useEffect(() => {
    if (openMenu && openMenu !== title) {
      setActivateMenu(false);
    }
  }, [openMenu]);

  const mouseEnter = () => {
    if (!breakpoints.smNav) {
      setActivateMenu(true);
      setOpenMenu(title);
    }
  }

  const mouseLeave = () => {
    if (!breakpoints.smNav) {
      setActivateMenu(false);
      setOpenMenu('');
    }
  }

  function menuItemClicked() {
    setActivateMenu(cur => !cur);
    setClickMenu(cur => !cur);
    setOpenMenu(title);
  }

  function deactivate() {
    setActivateMenu(false);
    setClickMenu(false);
    setOpenMenu('');
  };

  return (
    <FocusTrap 
      active={ activateMenu && clickMenu }
      focusTrapOptions={ {
        onDeactivate: () => deactivate(),
        allowOutsideClick: true,
        clickOutsideDeactivates: true,
    } }>
      <li className="menu-list-item is-dropdown"
        onMouseEnter={ mouseEnter }
        onMouseLeave={ mouseLeave }
      >
        <button
          type="button"
          className="menu-item"
          onClick={ menuItemClicked }
          aria-expanded={ activateMenu }
          aria-haspopup="true"
        >
          {title}
          <FontIcon icon="chevron-up" className="is-hidden-mobile-nav" />
          <FontIcon icon="arrow-right" className="is-hidden-tablet-nav" />
        </button>

        <div
          className="sub-menu-container"
          aria-hidden={ !activateMenu }
        >
          <div className="sub-menu">
            <ul className="sub-menu-group">
              <li className="mobile-back is-hidden-tablet-nav">
                <button type="button" onClick={ () => setActivateMenu(false) }><FontIcon icon="arrow-left" />Back</button>
              </li>
              {items.map((item, i) => {
                const [icon] = item.classes; // the first class supplied is used for the icon
                const hasDescription = !!item.description;

                return (
                  <li key={ i } className={ classNames('sub-menu-list-item', { 'sub-menu-list-item-short': !hasDescription }) }>
                    <div className="sub-menu-item">
                      <Link to={ item.url }>
                        <h3><FontIcon icon={ icon } />{item.title}</h3>
                      </Link>
                      { hasDescription &&
                        <div className="sub-menu-item-description">
                          <p className="is-hidden-mobile-nav">{item.description}</p>
                        </div>
                      }
                    </div>
                  </li>
                );
              })}
            </ul>
            { bottomItem &&
              <div className="sub-menu-link">
                <Link to={ bottomItem.url }><FontIcon icon={ bottomItem.classes[0] } />{bottomItem.title}</Link>
              </div>
            }
          </div>
        </div>
      </li>
    </FocusTrap>
  );
};